import type React from 'react'
import { COLORS, SHADOWS, RADIUS, FONT } from '../styles/tokens'

/* ── Shared setup-screen styles ──────────────────────────────────
 * Used by every game's SetupScreen. Values come from design tokens
 * so visual changes propagate here automatically.
 * ──────────────────────────────────────────────────────────────── */
export const ss: Record<string, React.CSSProperties> = {
  // Outer shell
  setupRoot: {
    minHeight: '100vh',
    padding:   '32px 24px 64px',
    maxWidth:  820,
    margin:    '0 auto',
  },
  setupInner: { display: 'flex', flexDirection: 'column', gap: 36 },

  // Back button
  backBtn: {
    alignSelf:    'flex-start',
    fontSize:     FONT.size.base,
    fontWeight:   700,
    color:        COLORS.green,
    padding:      '10px 18px',
    borderRadius: RADIUS.md,
    background:   COLORS.bgCard,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    boxShadow:    SHADOWS.subtle,
    cursor:       'pointer',
    minHeight:    44,
  },

  // Title block
  setupTitle: {
    fontFamily:   FONT.family.display,
    fontSize:     FONT.size.h1,
    fontWeight:   700,
    color:        COLORS.textPrimary,
    lineHeight:   1.1,
    marginBottom: 8,
  },
  setupSubtitle: { fontSize: FONT.size.md, color: COLORS.textBody, fontWeight: 500 },

  // Section heading
  optionLabel: {
    fontFamily:   FONT.family.display,
    fontSize:     FONT.size.xl,
    fontWeight:   600,
    color:        COLORS.textPrimary,
    marginBottom: 16,
  },

  // Theme selector
  themeRow: { display: 'flex', gap: 12, flexWrap: 'wrap' as const },
  themeBtn: {
    display:       'flex',
    flexDirection: 'column' as const,
    alignItems:    'center',
    gap:           6,
    padding:       '14px 18px',
    borderRadius:  RADIUS.card,
    background:    COLORS.bgCard,
    border:        `2px solid ${COLORS.borderWarm}`,
    boxShadow:     SHADOWS.subtle,
    cursor:        'pointer',
    transition:    'all 0.15s ease',
    minWidth:      80,
  },
  themeBtnActive: {
    border:     `2.5px solid ${COLORS.green}`,
    background: COLORS.greenSurface,
    boxShadow:  SHADOWS.active,
  },
  themeBtnLocked: { opacity: 0.55, cursor: 'pointer' as const },
  themeEmoji:     { fontSize: FONT.size.h2, lineHeight: 1 },
  themeCover:     { width: 48, height: 48, objectFit: 'contain' as const, borderRadius: RADIUS.sm, background: COLORS.bgCard },
  themeLabel:     { fontSize: FONT.size.sm, fontWeight: 700, color: COLORS.textDark },
  lockBadge: {
    position:   'absolute' as const,
    bottom:     -4,
    right:      -4,
    fontSize:   FONT.size.xs,
    lineHeight: 1,
  },

  // Difficulty selector
  diffRow: { display: 'flex', gap: 14, flexWrap: 'wrap' as const },
  diffBtn: {
    flex:          '1 1 140px',
    display:       'flex',
    flexDirection: 'column' as const,
    alignItems:    'center',
    gap:           4,
    padding:       '18px 20px',
    borderRadius:  RADIUS.card,
    background:    COLORS.bgCard,
    border:        `2px solid ${COLORS.borderWarm}`,
    boxShadow:     SHADOWS.subtle,
    cursor:        'pointer',
    transition:    'all 0.15s ease',
    minHeight:     48,
  },
  diffBtnActive: {
    border:     `2.5px solid ${COLORS.green}`,
    background: COLORS.greenSurface,
    boxShadow:  SHADOWS.active,
  },
  diffLabel: { fontSize: FONT.size.md, fontWeight: 700, color: COLORS.textDark },
  diffHint:  { fontSize: FONT.size.sm, color: COLORS.textSecondary, fontWeight: 600 },

  // Mode / option card (exported for games to use in their own grids)
  modeGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap:                 14,
  },
  modeCard: {
    display:       'flex',
    flexDirection: 'column' as const,
    alignItems:    'flex-start',
    gap:           6,
    padding:       '18px 20px',
    borderRadius:  RADIUS.card,
    background:    COLORS.bgCard,
    border:        `2px solid ${COLORS.borderWarm}`,
    boxShadow:     SHADOWS.subtle,
    transition:    'all 0.15s ease',
    textAlign:     'left' as const,
    cursor:        'pointer',
    minHeight:     48,
  },
  modeCardActive: {
    border:     `2.5px solid ${COLORS.green}`,
    background: COLORS.greenSurface,
    boxShadow:  SHADOWS.active,
  },

  // Start button
  startBtn: {
    width:        '100%',
    padding:      '20px',
    fontSize:     FONT.size.lg,
    fontWeight:   800,
    color:        COLORS.bgCard,
    background:   COLORS.gradientPrimary,
    borderRadius: RADIUS.lg,
    boxShadow:    SHADOWS.button,
    transition:   'transform 0.12s ease, box-shadow 0.12s ease',
    cursor:       'pointer',
    minHeight:    56,
  },
  startBtnDisabled: {
    opacity: 0.55,
    cursor:  'not-allowed' as const,
  },

  // Reusable action buttons (primary / secondary / ghost)
  btnPrimary: {
    fontFamily:   'Nunito, sans-serif',
    fontSize:     FONT.size.lg,
    fontWeight:   800,
    color:        COLORS.bgCard,
    background:   COLORS.gradientPrimary,
    border:       'none',
    borderRadius: RADIUS.md,
    padding:      '15px 24px',
    cursor:       'pointer',
    minHeight:    52,
  },
  btnSecondary: {
    fontFamily:   'Nunito, sans-serif',
    fontSize:     FONT.size.base,
    fontWeight:   700,
    color:        COLORS.greenDark,
    background:   COLORS.bgPage,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.md,
    padding:      '13px 24px',
    cursor:       'pointer',
    minHeight:    48,
  },
  btnGhost: {
    fontFamily: 'Nunito, sans-serif',
    fontSize:   FONT.size.sm,
    fontWeight: 700,
    color:      COLORS.textSecondary,
    background: 'transparent',
    border:     'none',
    cursor:     'pointer',
    padding:    '8px',
    minHeight:  40,
  },
}
